import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import Exception from '@/common/models/Exception';
import Market from '@/common/models/geo/Market';
import MediaType from '@/common/models/misc-api-models/MediaType';
import { FilterCollection, FilterComparisonOperator, QueryParams } from '@/common/models/query-params';
import UnitPackInventoryTargeting from '@/common/models/unit/UnitPackInventoryTargeting';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { DASH } from '@/globals';
import orgAppStore from '@/org-app/store';
import rootStore from '@/store';
import formatNumber from '@/utils/NumberFormatter';
import Validatable, { IEntityChange } from '@/validator/Validatable';
import i18n from '@/vueI18n';

import { getEntityProperties } from './UnitPack.entityProperties';

const type = 'UnitPack';

interface IUnitPackAPI {
  unitpack_id: number;
  external_id?: string;
  inventorytargeting_id?: string;
  active: boolean;
  archived: boolean;
  description?: string;
  name: string;
  unit_count: number;
  created_on_utc?: string;
  modified_on_utc?: string;
  owner_id: number;
}

export default class UnitPack extends Validatable {
  static isGettingMarkets = false;
  static marketsCache: Market[] = [];
  static isGettingMediaTypes = false;
  static mediaTypesCache: MediaType[] = [];

  apiObject: IUnitPackAPI;
  cachedOriginalObject: UnitPack;

  type: string;
  externalID?: string;
  inventoryTargetingId?: string;
  isActive: boolean;
  isArchived: boolean;
  description?: string;
  name: string;
  unitCount: number;
  createdOnUtc?: DateTime;
  modifiedOnUtc?: DateTime;
  ownerID?: number;
  timestamp: DateTime;
  _inventoryTargeting: UnitPackInventoryTargeting;

  constructor(
    id = -1,
    externalID?: string,
    inventoryTargetingId?: string,
    isActive = false,
    isArchived = false,
    description?: string,
    name = '',
    unitCount = 0,
    createdOnUtc?: DateTime,
    modifiedOnUtc?: DateTime,
    ownerID = orgAppStore?.state?.organization?.id,
  ) {
    super();
    this.type = type;
    this.id = id;
    this.externalID = externalID;
    this.inventoryTargetingId = inventoryTargetingId;
    this.isActive = isActive;
    this.isArchived = isArchived;
    this.description = description;
    this.name = name;
    this.unitCount = unitCount;
    this.createdOnUtc = createdOnUtc;
    this.modifiedOnUtc = modifiedOnUtc;
    this.ownerID = ownerID;
    this.timestamp = DateTime.local();
    this._inventoryTargeting = new UnitPackInventoryTargeting();
    this.apiObject = null;
    this.setupInventoryTargetingGettersSetters();
  }

  get inventoryTargeting() {
    return this._inventoryTargeting;
  }

  set inventoryTargeting(value) {
    this._inventoryTargeting = value;
    this.originalObject._inventoryTargeting = UnitPackInventoryTargeting.fromApi(value.toApi());
  }

  static {
    // When the user changes the language, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.languageChanged, () => {
      UnitPack.entityPropertiesCached = null;
    });

    // When the user logs out or changes the organization, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.logout, () => {
      UnitPack.entityPropertiesCached = null;
    });
    GlobalEventBus.$on(GlobalEvents.organizationChanged, () => {
      UnitPack.entityPropertiesCached = null;
    });
  }

  get originalObject(): UnitPack {
    if (this.apiObject == null) return null;
    if (this.cachedOriginalObject == null) {
      this.cachedOriginalObject = UnitPack.fromApi(this.apiObject);
    }
    return this.cachedOriginalObject;
  }

  get endpoint() {
    return `/unit-packs/${this.id}`;
  }

  get isActiveDescription() {
    if (this.isActive) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get isArchivedDescription() {
    if (this.isArchived) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get unitCountFormatted() {
    return formatNumber(this.unitCount, '0,0');
  }

  get createdOnFormatted() {
    return this.createdOnUtc != null ? this.createdOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get modifiedOnFormatted() {
    return this.modifiedOnUtc != null ? this.modifiedOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get createdOn() {
    return this.createdOnUtc != null ? this.createdOnUtc.toLocal() : null;
  }

  get modifiedOn() {
    return this.modifiedOnUtc != null ? this.modifiedOnUtc.toLocal() : null;
  }

  get idAndName() {
    return `${this.externalID} - ${this.name}`;
  }

  static fromApi(apiObject: IUnitPackAPI) {
    const unitPack = new UnitPack(
      apiObject.unitpack_id,
      apiObject.external_id,
      apiObject.inventorytargeting_id,
      apiObject.active,
      apiObject.archived,
      apiObject.description,
      apiObject.name,
      apiObject.unit_count,
      apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      apiObject.modified_on_utc != null ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      apiObject.owner_id,
    );
    unitPack.apiObject = apiObject;
    return unitPack;
  }

  setupInventoryTargetingGettersSetters() {
    // don't setup getters & setters for these properties
    const excludeGetterSetter = ['id', 'extraValidation', 'rawValues', 'changes'];

    for (const key in this._inventoryTargeting) {
      if (!excludeGetterSetter.includes(key)) {
        Object.defineProperty(this, key, {
          get: () => this._inventoryTargeting[key],
          set: (value) => {
            if (this._inventoryTargeting[key] !== value) {
              this._inventoryTargeting[key] = value;
            }
          },
        });
      }
    }
  }

  toApi(): IUnitPackAPI {
    return {
      active: this.isActive,
      archived: this.isArchived,
      external_id: this.externalID,
      unitpack_id: this.id,
      inventorytargeting_id: this.inventoryTargetingId,
      name: this.name,
      description: this.description,
      unit_count: this.unitCount,
      owner_id: this.ownerID,
    };
  }

  async getChanges(): Promise<IEntityChange<UnitPack>[]> {
    if (this.isNew) return [];

    return super.getValidatableChanges<UnitPack>(this.originalObject, UnitPack.entityProperties);
  }

  static fromObject(obj) {
    return super.fromObject(obj, new UnitPack());
  }

  static validatorCreateRules() {
    return {
      name: 'required|max:100|not_empty_or_whitespace',
      externalID: 'required|not_empty_or_whitespace',
    };
  }

  static get entityName() {
    return type;
  }

  static async getEntityIDsFromModels(models) {
    if (models.length === 0) return [];

    try {
      const externalIDs = models.map((x) => x.externalID);
      const getUnitPacksAction = ({ queryParams, showLoader }: { queryParams?: QueryParams; showLoader: boolean }) =>
        rootStore.dispatch('orgApp/getUnitPacks', { queryParams, showLoader });
      const qp = QueryParams.all(FilterCollection.for('external_id', externalIDs, FilterComparisonOperator.IN));
      qp.include = ['unitpack_id', 'external_id'];
      const response = await qp.run(getUnitPacksAction);
      const entities = response.items;
      return models.map((model) => ({ model, id: entities.find((x) => x.externalID === model.externalID)?.id }));
    } catch (e) {
      throw new Exception('Failed to get entity IDs.');
    }
  }

  static async assignIDs(entities) {
    if (entities.length === 0) return [];

    const existingIDMapping = await UnitPack.getEntityIDsFromModels(entities);
    entities.forEach((x) => {
      x.id = existingIDMapping.find((y) => y.model === x)?.id || -1;
    });
    return entities;
  }

  static async patchEntities(entities) {
    try {
      const payload = this.getPatchPayload(entities);
      await rootStore.dispatch('orgApp/patchUnitPacks', payload);
    } catch (e) {
      throw new Exception('Failed to patch unitPacks.');
    }
  }

  static async getEntities(ids) {
    if (ids.length === 0) return [];

    try {
      const queryParams = QueryParams.all(FilterCollection.for('unitpack_id', ids, FilterComparisonOperator.IN));
      queryParams.showLoader = false;
      const response = await rootStore.dispatch('orgApp/getUnitPacks', {
        queryParams,
        cancelTokenSource: queryParams.cancelTokenSource,
        showLoader: false,
      });
      return response.items;
    } catch (e) {
      throw new Exception('Failed to get unitPacks.');
    }
  }

  static get entityProperties() {
    if (UnitPack.entityPropertiesCached != null) {
      return UnitPack.entityPropertiesCached;
    }
    UnitPack.entityPropertiesCached = getEntityProperties();
    return UnitPack.entityPropertiesCached;
  }
}
